import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

/**
 * Makes a container (table row, card) clickable for navigation while leaving the
 * interactive elements inside it (links, buttons, lightbox dialogs) working.
 *
 * Ignores clicks on interactive descendants, honors text selection, and routes
 * modifier / middle clicks to a new tab. Turbo.visit is a top-level Drive visit,
 * so it never gets trapped inside an enclosing <turbo-frame>.
 *
 * Usage:
 *   <div data-controller="clickable-row"
 *        data-clickable-row-url-value="/arena/text-to-image/foo"
 *        class="cursor-pointer">
 *     ...
 *   </div>
 */
export default class ClickableRowController extends Controller {
  static values = {
    url: String,
  }

  declare urlValue: string

  private boundNavigate = this.navigate.bind(this)
  private boundAux = this.auxNavigate.bind(this)

  connect() {
    this.element.addEventListener("click", this.boundNavigate)
    this.element.addEventListener("auxclick", this.boundAux)
  }

  disconnect() {
    this.element.removeEventListener("click", this.boundNavigate)
    this.element.removeEventListener("auxclick", this.boundAux)
  }

  navigate(event: MouseEvent): void {
    if (this.shouldIgnore(event)) return

    // A drag-to-select inside the card shouldn't navigate.
    if (window.getSelection()?.toString()) return

    // Modifier click → new tab, matching native link behavior.
    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      window.open(this.urlValue, "_blank", "noopener")
      return
    }

    Turbo.visit(this.urlValue)
  }

  // Middle-click → new tab.
  auxNavigate(event: MouseEvent): void {
    if (event.button !== 1 || this.shouldIgnore(event)) return
    event.preventDefault()
    window.open(this.urlValue, "_blank", "noopener")
  }

  // Let real interactive elements (and the lightbox dialog) handle their own
  // clicks. `form` is intentionally absent so cards inside forms stay clickable.
  private shouldIgnore(event: MouseEvent): boolean {
    const target = event.target as HTMLElement | null
    return !!target?.closest(
      "a, button, input, select, textarea, dialog, [data-action], [data-turbo-confirm]"
    )
  }
}
